import { useLayoutEffect } from 'react'

import {
  clearFloatingChromeObstruction,
  FLOATING_CHROME_LAUNCHER_CLEARANCE_PX,
  setFloatingChromeObstruction,
} from './floating_chrome_insets'

const INTERCOM_LAUNCHER_CORNER_SIZE_PX = 72

const intersectsLauncherCorner = (rect, viewportWidth, viewportHeight) => (
  rect.right > viewportWidth - INTERCOM_LAUNCHER_CORNER_SIZE_PX
  && rect.left < viewportWidth
  && rect.bottom > viewportHeight - INTERCOM_LAUNCHER_CORNER_SIZE_PX
  && rect.top < viewportHeight
)

export const measureElementFloatingChromeInsets = (element) => {
  if (!element || typeof window === 'undefined') {
    return { right: 0, bottom: 0 }
  }

  const rect = element.getBoundingClientRect()
  const { innerWidth: viewportWidth, innerHeight: viewportHeight } = window

  if (!intersectsLauncherCorner(rect, viewportWidth, viewportHeight)) {
    return { right: 0, bottom: 0 }
  }

  const clearance = FLOATING_CHROME_LAUNCHER_CLEARANCE_PX
  const cornerTop = viewportHeight - INTERCOM_LAUNCHER_CORNER_SIZE_PX
  const elementHeight = rect.bottom - rect.top

  const right = Math.max(0, viewportWidth - rect.left + clearance)

  // Tall right rails / side panels overlap the launcher corner horizontally only.
  // Using viewportHeight - rect.top for those would report ~full viewport height and
  // push Intercom to the top of the page.
  let bottom = 0
  if (rect.bottom > cornerTop) {
    if (rect.top >= cornerTop) {
      bottom = Math.max(0, viewportHeight - rect.top + clearance)
    } else if (elementHeight <= INTERCOM_LAUNCHER_CORNER_SIZE_PX * 2) {
      bottom = Math.max(0, rect.bottom - cornerTop + clearance)
    }
  }

  return { right, bottom }
}

export const useMeasureFloatingChromeObstruction = (id, ref, enabled = true) => {
  useLayoutEffect(() => {
    if (!enabled || typeof window === 'undefined') {
      clearFloatingChromeObstruction(id)
      return undefined
    }

    const measure = () => {
      const element = ref?.current

      if (!element) {
        clearFloatingChromeObstruction(id)
        return
      }

      setFloatingChromeObstruction(id, measureElementFloatingChromeInsets(element))
    }

    measure()

    const resizeObserver = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(measure)
      : null
    const element = ref.current

    if (element && resizeObserver) {
      resizeObserver.observe(element)
    }

    window.addEventListener('resize', measure)
    window.addEventListener('scroll', measure, true)

    return () => {
      resizeObserver?.disconnect()
      window.removeEventListener('resize', measure)
      window.removeEventListener('scroll', measure, true)
      clearFloatingChromeObstruction(id)
    }
  }, [id, ref, enabled])
}
